import { useRestApiProvider } from "@jugl-web/rest-api";
import { TaskDefaultStatusId } from "@jugl-web/rest-api/tasks";
import { TaskCustomStatusDto } from "@jugl-web/rest-api/tasks/models/TaskCustomStatus";
import { mapIndexesToOrder } from "@jugl-web/utils/array/mapIndexesToOrder";
import { reorder } from "@jugl-web/utils/reorder";
import partition from "lodash/partition";
import { useCallback, useMemo } from "react";
import { DEFAULT_TASK_FIELD_COLOR } from "../../custom-fields/consts";
import {
  DEFAULT_COMPLETED_STATUS_COLOR,
  DEFAULT_PENDING_STATUS_COLOR,
} from "../consts";
import { getStatusColor } from "../utils/getStatusColor";
import { useTaskStatusesGetters } from "./useTaskStatusesGetters";

export interface UseTaskStatusesOptions {
  entityId: string;
  boardId: string | null;
}

const taskDefaultStatusIds = Object.values<string>(TaskDefaultStatusId);

export const FALLBACK_PENDING_STATUS: TaskCustomStatusDto = {
  id: TaskDefaultStatusId.pending,
  order: 1,
  text: "Open",
  color: DEFAULT_PENDING_STATUS_COLOR,
  created_by: null,
};

export const FALLBACK_COMPLETED_STATUS: TaskCustomStatusDto = {
  id: TaskDefaultStatusId.completed,
  order: 2,
  text: "Completed",
  color: DEFAULT_COMPLETED_STATUS_COLOR,
  created_by: null,
};

export const isDefaultStatus = (statusId: string) =>
  taskDefaultStatusIds.includes(statusId);

/**
 * Source of all task statuses, including entity-level and board-level statuses.
 * If `boardId` is not provided, entity-level statuses will be returned.
 */
export const useTaskStatuses = ({
  entityId,
  boardId,
}: UseTaskStatusesOptions) => {
  const { tasksApi } = useRestApiProvider();

  const { getStatusesByBoard, produceGetStatusById } = useTaskStatusesGetters({
    entityId,
  });

  const getStatusById = useMemo(
    () => produceGetStatusById(boardId),
    [boardId, produceGetStatusById]
  );

  const statusesSource = useMemo(
    () => getStatusesByBoard(boardId),
    [boardId, getStatusesByBoard]
  );

  const [defaultStatuses, customStatuses] = useMemo(
    () => partition(statusesSource, (status) => isDefaultStatus(status.id)),
    [statusesSource]
  );

  const pendingStatus = useMemo(
    () =>
      defaultStatuses.find((s) => s.id === TaskDefaultStatusId.pending) ||
      FALLBACK_PENDING_STATUS,
    [defaultStatuses]
  );

  const completedStatus = useMemo(
    () =>
      defaultStatuses.find((s) => s.id === TaskDefaultStatusId.completed) ||
      FALLBACK_COMPLETED_STATUS,
    [defaultStatuses]
  );

  const customStatusesSorted = useMemo(
    () => customStatuses.sort((a, b) => a.order - b.order),
    [customStatuses]
  );

  const allStatusesSorted = useMemo<TaskCustomStatusDto[]>(
    () => [pendingStatus, ...customStatusesSorted, completedStatus],
    [completedStatus, pendingStatus, customStatusesSorted]
  );

  const allStatusesSortedWithoutCompleted = useMemo(
    () =>
      allStatusesSorted.filter(
        (status) => status.id !== TaskDefaultStatusId.completed
      ),
    [allStatusesSorted]
  );

  const getStatusColorById = useCallback(
    (statusId: string) => {
      const matchingStatus = getStatusById(statusId);

      if (!matchingStatus) {
        return DEFAULT_TASK_FIELD_COLOR;
      }

      return getStatusColor(matchingStatus);
    },
    [getStatusById]
  );

  const getStatusByIdWithFallback = useCallback(
    (statusId: string): TaskCustomStatusDto => {
      const matchingStatus = getStatusById(statusId);

      if (matchingStatus) {
        return matchingStatus;
      }

      // Return default status as a fallback
      return pendingStatus;
    },
    [getStatusById, pendingStatus]
  );

  // #region Mutations
  const [
    createTaskCustomStatusMutation,
    { isLoading: isCreatingCustomStatus },
  ] = tasksApi.useCreateTaskCustomStatusMutation();

  const [
    updateTaskCustomStatusesMutation,
    { isLoading: isUpdatingCustomStatuses },
  ] = tasksApi.useUpdateTaskCustomStatusesMutation();

  const [
    deleteTaskCustomStatusMutation,
    { isLoading: isDeletingCustomStatus },
  ] = tasksApi.useDeleteTaskCustomStatusMutation();

  const createCustomStatus = useCallback(
    async (newCustomStatus: { text: string; color: string }) =>
      new Promise<TaskCustomStatusDto>((resolve, reject) => {
        if (!boardId) {
          reject(new Error("Board ID is required"));
          return;
        }

        const newOrder =
          customStatusesSorted.length > 0
            ? customStatusesSorted[customStatusesSorted.length - 1].order + 1
            : 1;

        createTaskCustomStatusMutation({
          entityId,
          boardId,
          customStatus: { ...newCustomStatus, order: newOrder },
        }).then((response) => {
          if ("data" in response) {
            resolve(response.data);
          } else {
            reject(response.error);
          }
        });
      }),
    [boardId, customStatusesSorted, createTaskCustomStatusMutation, entityId]
  );

  const updateCustomStatus = useCallback(
    (statusId: string, updatedText: string, updatedColor: string) =>
      new Promise((resolve, reject) => {
        if (!boardId) {
          reject(new Error("Board ID is required"));
          return;
        }

        updateTaskCustomStatusesMutation({
          entityId,
          boardId,
          customStatuses: statusesSource.map((status) =>
            status.id === statusId
              ? { ...status, text: updatedText, color: updatedColor }
              : status
          ),
        }).then((response) => {
          if ("data" in response) {
            resolve(response.data);
          } else {
            reject(response.error);
          }
        });
      }),
    [boardId, statusesSource, entityId, updateTaskCustomStatusesMutation]
  );

  const deleteCustomStatus = useCallback(
    (customStatusId: string, altStatusId: string) =>
      new Promise((resolve, reject) => {
        if (!boardId) {
          reject(new Error("Board ID is required"));
          return;
        }

        deleteTaskCustomStatusMutation({
          entityId,
          boardId,
          statusId: customStatusId,
          altStatusId,
        }).then((response) => {
          if ("data" in response) {
            resolve(response.data);
          } else {
            reject(new Error("Failed to delete custom status"));
          }
        });
      }),
    [boardId, deleteTaskCustomStatusMutation, entityId]
  );

  const reorderCustomStatus = useCallback(
    (startIndex: number, endIndex: number) =>
      new Promise((resolve, reject) => {
        if (!boardId) {
          reject(new Error("Board ID is required"));
          return;
        }

        const updatedCustomStatuses = mapIndexesToOrder(
          reorder(customStatusesSorted, startIndex, endIndex)
        );

        const updatedStatuses = [
          pendingStatus,
          ...updatedCustomStatuses,
          completedStatus,
        ];

        updateTaskCustomStatusesMutation({
          entityId,
          boardId,
          customStatuses: updatedStatuses,
        }).then((response) => {
          if ("data" in response) {
            resolve(response.data);
          } else {
            reject(new Error("Failed to reorder custom statuses"));
          }
        });
      }),
    [
      boardId,
      completedStatus,
      entityId,
      pendingStatus,
      customStatusesSorted,
      updateTaskCustomStatusesMutation,
    ]
  );
  // #endregion

  return {
    pendingStatus,
    completedStatus,
    fallbackStatus: pendingStatus,
    customStatusesSorted,
    allStatusesSorted,
    allStatusesSortedWithoutCompleted,
    getStatusById,
    getStatusByIdWithFallback,
    getStatusColorById,

    // Mutations
    createCustomStatus,
    updateCustomStatus,
    deleteCustomStatus,
    reorderCustomStatus,
    isCreatingCustomStatus,
    isUpdatingCustomStatuses,
    isDeletingCustomStatus,
  };
};
